/**
 * API Service per gestione prompt dal database Neon
 * Sostituisce localStorage con chiamate API backend
 */

import { API_URL } from '../config';

export interface Prompt {
  id: string;
  user_id: string;
  node_type: string;
  name: string;
  content: string;
  category_id?: string;
  metadata?: Record<string, any>;
  tags?: string[];
  is_shared?: boolean;
  created_at: string;
  updated_at: string;
}

export interface Category {
  id: string;
  name: string;
  parent_id?: string;
  user_id: string;
  created_at: string;
}

export interface PromptCreate {
  node_type: string;
  name: string;
  content: string;
  category_id?: string;
  metadata?: Record<string, any>;
  tags?: string[];
  is_shared?: boolean;
}

export interface PromptUpdate {
  name?: string;
  content?: string;
  category_id?: string;
  metadata?: Record<string, any>;
  tags?: string[];
  is_shared?: boolean;
}

// Note: Auth0 token extraction is handled via useAuth0UserId hook in components
// This service uses X-User-ID header for API calls

/**
 * Crea un nuovo prompt
 */
export async function createPrompt(
  prompt: PromptCreate,
  userId: string
): Promise<Prompt> {
  const response = await fetch(`${API_URL}/api/prompts`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-User-ID': userId,
    },
    body: JSON.stringify(prompt),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Errore creazione prompt');
  }

  return response.json();
}

/**
 * Lista prompt con filtri opzionali
 */
export async function listPrompts(
  userId: string,
  options?: {
    node_type?: string;
    category_id?: string;
    search?: string;
    limit?: number;
    offset?: number;
  }
): Promise<{ prompts: Prompt[]; count: number }> {
  const params = new URLSearchParams();
  if (options?.node_type) params.append('node_type', options.node_type);
  if (options?.category_id) params.append('category_id', options.category_id);
  if (options?.search) params.append('search', options.search);
  if (options?.limit) params.append('limit', options.limit.toString());
  if (options?.offset) params.append('offset', options.offset.toString());

  const response = await fetch(`${API_URL}/api/prompts?${params.toString()}`, {
    method: 'GET',
    headers: {
      'X-User-ID': userId,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Errore recupero prompt');
  }

  return response.json();
}

/**
 * Recupera un prompt per ID
 */
export async function getPrompt(
  promptId: string,
  userId: string
): Promise<Prompt> {
  const response = await fetch(`${API_URL}/api/prompts/${promptId}`, {
    method: 'GET',
    headers: {
      'X-User-ID': userId,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Prompt non trovato');
  }

  return response.json();
}

/**
 * Aggiorna un prompt
 */
export async function updatePrompt(
  promptId: string,
  prompt: PromptUpdate,
  userId: string
): Promise<Prompt> {
  const response = await fetch(`${API_URL}/api/prompts/${promptId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'X-User-ID': userId,
    },
    body: JSON.stringify(prompt),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Errore aggiornamento prompt');
  }

  return response.json();
}

/**
 * Elimina un prompt
 */
export async function deletePrompt(
  promptId: string,
  userId: string
): Promise<void> {
  const response = await fetch(`${API_URL}/api/prompts/${promptId}`, {
    method: 'DELETE',
    headers: {
      'X-User-ID': userId,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Errore eliminazione prompt');
  }
}

/**
 * Lista categorie
 */
export async function listCategories(userId: string): Promise<Category[]> {
  const response = await fetch(`${API_URL}/api/prompts/categories`, {
    method: 'GET',
    headers: {
      'X-User-ID': userId,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Errore recupero categorie');
  }

  const data = await response.json();
  return data.categories || [];
}

/**
 * Crea una categoria
 */
export async function createCategory(
  name: string,
  userId: string,
  parentId?: string
): Promise<Category> {
  const response = await fetch(`${API_URL}/api/prompts/categories`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-User-ID': userId,
    },
    body: JSON.stringify({ name, parent_id: parentId }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Errore creazione categoria');
  }

  return response.json();
}

/**
 * Elimina una categoria
 */
export async function deleteCategory(
  categoryId: string,
  userId: string
): Promise<void> {
  const response = await fetch(`${API_URL}/api/prompts/categories/${categoryId}`, {
    method: 'DELETE',
    headers: {
      'X-User-ID': userId,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Errore eliminazione categoria');
  }
}

/**
 * Health check
 */
export async function healthCheck(): Promise<{ status: string; schema: string; message: string }> {
  const response = await fetch(`${API_URL}/api/prompts/health`);

  if (!response.ok) {
    throw new Error('Health check fallito');
  }

  return response.json();
}

/**
 * Debug endpoint per vedere tutti i fixed_prompt
 */
export async function debugFixedPrompts(userId: string): Promise<any> {
  const response = await fetch(`${API_URL}/api/prompts/debug-fixed-prompts`, {
    method: 'GET',
    headers: {
      'X-User-ID': userId,
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || 'Errore debug fixed prompts');
  }

  return response.json();
}

